import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const asList = (payload) =>
  Array.isArray(payload)
    ? payload
    : payload?.content || payload?.data || payload?.items || [];

const formatDateTime = (value) => {
  if (!value) return "Not run";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const normalizeStatus = (value) =>
  firstText(value, "NEEDS_REVIEW")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());

const policyStatusTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "COMPLIANT") return "green";
  if (
    normalized === "NON_COMPLIANT" ||
    normalized === "OVERDUE" ||
    normalized === "ERROR"
  )
    return "red";
  if (
    normalized === "AT_RISK" ||
    normalized === "INSUFFICIENT_EVIDENCE" ||
    normalized === "NEEDS_REVIEW"
  )
    return "amber";
  if (normalized === "NOT_APPLICABLE") return "gray";
  return "blue";
};

const policyTitle = (policy) =>
  firstText(
    policy.compliancePolicyName,
    policy.compliancePolicyTitle,
    policy.inventoryItemName,
    policy.inventoryItemTitle,
    policy.name,
    policy.title,
    policy.compliancePolicy?.name,
    policy.compliancePolicy?.title,
    policy.complianceInventoryItem?.name,
    policy.complianceInventoryItem?.title,
    policy.inventoryItem?.name,
    policy.inventoryItem?.title,
    "Compliance policy",
  );

const policyCode = (policy) =>
  firstText(
    policy.compliancePolicyCode,
    policy.inventoryItemCode,
    policy.code,
    policy.compliancePolicy?.code,
    policy.complianceInventoryItem?.code,
    policy.inventoryItem?.code,
  );

const runDetails = (policy) => {
  const details =
    policy.recentRun?.results ||
    policy.latestRunResults ||
    policy.lastRunResults;
  if (!details) return "No run results yet.";
  return typeof details === "string" ? details : JSON.stringify(details);
};

const policyDetail = (policy, ...keys) => {
  const masterPolicy =
    policy?.compliancePolicy ||
    policy?.complianceInventoryItem ||
    policy?.inventoryItem ||
    {};
  return firstText(
    ...keys.map((key) => policy?.[key]),
    ...keys.map((key) => masterPolicy?.[key]),
  );
};

const prettyJson = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const latestRunValue = (policy) =>
  firstText(
    policy.latestRunAt,
    policy.lastRunAt,
    policy.recentRun?.createdAt,
    policy.recentRun?.runAt,
  );

const latestRunTime = (policy) => {
  const value = latestRunValue(policy);
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const policySearchText = (policy) =>
  [
    policyTitle(policy),
    policyCode(policy),
    firstText(policy.status, policy.latestStatus, policy.lastRunStatus),
    policyDetail(policy, "state"),
    policyDetail(policy, "country"),
    policyDetail(policy, "metric"),
    policyDetail(policy, "description"),
    policyDetail(policy, "expectation"),
    policyDetail(policy, "threshold"),
    policyDetail(policy, "reference"),
    runDetails(policy),
  ]
    .join(" ")
    .toLowerCase();

const compareText = (left, right) =>
  left.localeCompare(right, undefined, { sensitivity: "base" });

const policyIdentity = (policy) => ({
  workspacePolicyId: firstText(
    policy.id,
    policy.workspaceCompliancePolicyId,
    policy.policyId,
  ),
  compliancePolicyId: firstText(
    policy.compliancePolicyId,
    policy.compliancePolicy?.id,
  ),
});

export default function ComplianceInventoryPage() {
  const { selectedWorkspaceId } = useWorkspace();
  const [workspacePolicies, setWorkspacePolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingPolicyId, setSavingPolicyId] = useState("");
  const [viewPolicy, setViewPolicy] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [enabledFilter, setEnabledFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("policy");
  const [sortDirection, setSortDirection] = useState("asc");
  const [bulkAction, setBulkAction] = useState("");

  const loadPolicies = () => {
    setLoading(true);
    setError("");
    if (!selectedWorkspaceId) {
      setWorkspacePolicies([]);
      setLoading(false);
      return;
    }
    careService.workspaceCompliancePolicies
      .list(selectedWorkspaceId, { wid: selectedWorkspaceId })
      .then((payload) => setWorkspacePolicies(asList(payload)))
      .catch((err) => {
        setWorkspacePolicies([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load compliance policies.",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPolicies();
  }, [selectedWorkspaceId]);

  const replacePolicy = (updatedPolicy) => {
    const updatedCompliancePolicyId = firstText(
      updatedPolicy.compliancePolicyId,
      updatedPolicy.compliancePolicy?.id,
    );
    const updatedWorkspacePolicyId = firstText(
      updatedPolicy.id,
      updatedPolicy.workspaceCompliancePolicyId,
      updatedPolicy.policyId,
    );
    setWorkspacePolicies((current) =>
      current.map((policy) =>
        firstText(policy.compliancePolicyId, policy.compliancePolicy?.id) ===
          updatedCompliancePolicyId ||
        firstText(
          policy.id,
          policy.workspaceCompliancePolicyId,
          policy.policyId,
        ) === updatedWorkspacePolicyId
          ? { ...policy, ...updatedPolicy }
          : policy,
      ),
    );
  };

  const savePolicyEnabled = (policy, nextEnabled) => {
    const { workspacePolicyId, compliancePolicyId } = policyIdentity(policy);
    if (!workspacePolicyId && !nextEnabled) return null;
    return workspacePolicyId
      ? careService.workspaceCompliancePolicies.setEnabled(
          selectedWorkspaceId,
          workspacePolicyId,
          nextEnabled,
          { wid: selectedWorkspaceId },
        )
      : careService.workspaceCompliancePolicies.create(
          selectedWorkspaceId,
          { compliancePolicyId, enabled: true, status: "NEEDS_REVIEW" },
          { wid: selectedWorkspaceId },
        );
  };

  const togglePolicyEnabled = (policy) => {
    if (!selectedWorkspaceId) return;
    const { workspacePolicyId, compliancePolicyId } = policyIdentity(policy);
    const nextEnabled = policy.enabled === false;
    if (!workspacePolicyId && !nextEnabled) return;
    const savingKey = firstText(workspacePolicyId, compliancePolicyId);
    setSavingPolicyId(savingKey);
    setError("");
    const request = savePolicyEnabled(policy, nextEnabled);
    request
      .then(replacePolicy)
      .catch((err) =>
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to update compliance policy.",
        ),
      )
      .finally(() => setSavingPolicyId(""));
  };

  const statusOptions = useMemo(() => {
    const statuses = new Set(
      workspacePolicies.map((policy) =>
        firstText(
          policy.status,
          policy.latestStatus,
          policy.lastRunStatus,
          "NEEDS_REVIEW",
        ),
      ),
    );
    return Array.from(statuses).sort(compareText);
  }, [workspacePolicies]);

  const visiblePolicies = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const rows = workspacePolicies.filter((policy) => {
      const status = firstText(
        policy.status,
        policy.latestStatus,
        policy.lastRunStatus,
        "NEEDS_REVIEW",
      );
      const matchesSearch = !query || policySearchText(policy).includes(query);
      const matchesStatus = statusFilter === "ALL" || status === statusFilter;
      const matchesEnabled =
        enabledFilter === "ALL" ||
        (enabledFilter === "ENABLED" && policy.enabled !== false) ||
        (enabledFilter === "DISABLED" && policy.enabled === false);
      return matchesSearch && matchesStatus && matchesEnabled;
    });

    const direction = sortDirection === "desc" ? -1 : 1;
    return [...rows].sort((left, right) => {
      let result = 0;
      if (sortBy === "enabled") {
        result =
          Number(left.enabled !== false) - Number(right.enabled !== false);
      } else if (sortBy === "status") {
        result = compareText(
          firstText(
            left.status,
            left.latestStatus,
            left.lastRunStatus,
            "NEEDS_REVIEW",
          ),
          firstText(
            right.status,
            right.latestStatus,
            right.lastRunStatus,
            "NEEDS_REVIEW",
          ),
        );
      } else if (sortBy === "latestRun") {
        result = latestRunTime(left) - latestRunTime(right);
      } else if (sortBy === "code") {
        result = compareText(policyCode(left), policyCode(right));
      } else {
        result = compareText(policyTitle(left), policyTitle(right));
      }

      if (result === 0)
        result = compareText(policyTitle(left), policyTitle(right));
      return result * direction;
    });
  }, [
    enabledFilter,
    searchQuery,
    sortBy,
    sortDirection,
    statusFilter,
    workspacePolicies,
  ]);

  const bulkTargets = useMemo(() => {
    if (bulkAction === "enable") {
      return workspacePolicies.filter(
        (policy) =>
          policy.enabled === false && policyIdentity(policy).compliancePolicyId,
      );
    }
    if (bulkAction === "disable") {
      return workspacePolicies.filter(
        (policy) =>
          policy.enabled !== false && policyIdentity(policy).workspacePolicyId,
      );
    }
    return [];
  }, [bulkAction, workspacePolicies]);

  const runBulkAction = async () => {
    if (!selectedWorkspaceId || !bulkAction || !bulkTargets.length) {
      setBulkAction("");
      return;
    }

    const nextEnabled = bulkAction === "enable";
    setSavingPolicyId(`bulk-${bulkAction}`);
    setError("");
    try {
      const updatedPolicies =
        await careService.workspaceCompliancePolicies.setAllEnabled(
          selectedWorkspaceId,
          nextEnabled,
          { wid: selectedWorkspaceId },
        );
      setWorkspacePolicies(asList(updatedPolicies));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          `Unable to ${nextEnabled ? "enable" : "disable"} all compliance policies.`,
      );
    } finally {
      setSavingPolicyId("");
      setBulkAction("");
    }
  };

  return (
    <CareShell className="dashboard-content app-page-content compliance-inventory-page">
      <section className="care-page-hero">
        <div>
          <span>Washington AFH compliance</span>
          <h1>Compliance Policies</h1>
          <p>
            Review the compliance requirements and tracking status for this care
            facility.
          </p>
        </div>
        <button
          className="mini-btn primary"
          type="button"
          onClick={loadPolicies}
          disabled={loading}
        >
          <i className={`ti ${loading ? "ti-loader-2" : "ti-refresh"}`} />{" "}
          Refresh
        </button>
      </section>

      {error ? <p className="form-error">{error}</p> : null}

      <Panel
        title="Compliance Policies"
        actionNode={
          <div className="compliance-policy-toolbar">
            <label className="compliance-policy-search">
              <i className="ti ti-search" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search policies"
                aria-label="Search compliance policies"
              />
            </label>
            <label>
              <span>Status</span>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter by status"
              >
                <option value="ALL">All statuses</option>
                {statusOptions.map((status) => (
                  <option value={status} key={status}>
                    {normalizeStatus(status)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Enabled</span>
              <select
                value={enabledFilter}
                onChange={(event) => setEnabledFilter(event.target.value)}
                aria-label="Filter by enabled state"
              >
                <option value="ALL">All</option>
                <option value="ENABLED">Enabled</option>
                <option value="DISABLED">Disabled</option>
              </select>
            </label>
            <label>
              <span>Sort</span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                aria-label="Sort compliance policies"
              >
                <option value="policy">Policy</option>
                <option value="code">Code</option>
                <option value="enabled">Enabled</option>
                <option value="status">Status</option>
                <option value="latestRun">Latest run</option>
              </select>
            </label>
            <button
              className="mini-btn"
              type="button"
              onClick={() =>
                setSortDirection((current) =>
                  current === "asc" ? "desc" : "asc",
                )
              }
            >
              <i
                className={`ti ${sortDirection === "asc" ? "ti-sort-ascending" : "ti-sort-descending"}`}
              />
              {sortDirection === "asc" ? "Asc" : "Desc"}
            </button>
            <div className="compliance-policy-bulk-actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => setBulkAction("enable")}
                disabled={
                  loading ||
                  savingPolicyId.startsWith("bulk-") ||
                  !workspacePolicies.some((policy) => policy.enabled === false)
                }
              >
                <i className="ti ti-toggle-right" /> Enable All
              </button>
              <button
                className="mini-btn"
                type="button"
                onClick={() => setBulkAction("disable")}
                disabled={
                  loading ||
                  savingPolicyId.startsWith("bulk-") ||
                  !workspacePolicies.some(
                    (policy) =>
                      policy.enabled !== false &&
                      policyIdentity(policy).workspacePolicyId,
                  )
                }
              >
                <i className="ti ti-toggle-left" /> Disable All
              </button>
            </div>
            <span className="compliance-policy-result-count">
              {visiblePolicies.length} of {workspacePolicies.length}
            </span>
          </div>
        }
      >
        <div
          className="resident-tab-list compliance-policy-list"
          style={{
            "--resident-record-columns":
              "58px minmax(220px, 1.3fr) 100px 130px 155px 110px",
          }}
        >
          <div className="resident-tab-list-row resident-tab-list-head-row">
            <span>#</span>
            <span>Policy</span>
            <span>Enabled</span>
            <span>Status</span>
            <span>Latest run</span>
            <span>Actions</span>
          </div>
          {visiblePolicies.map((policy, index) => {
            const status = firstText(
              policy.status,
              policy.latestStatus,
              policy.lastRunStatus,
              "NEEDS_REVIEW",
            );
            const latestRun = firstText(
              policy.latestRunAt,
              policy.lastRunAt,
              policy.recentRun?.createdAt,
              policy.recentRun?.runAt,
            );
            const workspacePolicyId = firstText(
              policy.id,
              policy.workspaceCompliancePolicyId,
              policy.policyId,
            );
            const compliancePolicyId = firstText(
              policy.compliancePolicyId,
              policy.compliancePolicy?.id,
            );
            const savingKey = firstText(workspacePolicyId, compliancePolicyId);
            return (
              <article
                className="resident-tab-list-row"
                key={firstText(
                  policy.id,
                  policy.compliancePolicyId,
                  policy.policyId,
                  `${policyCode(policy)}-${index}`,
                )}
              >
                <span>{index + 1}</span>
                <span>
                  <strong>{policyTitle(policy)}</strong>
                  <small>{policyCode(policy)}</small>
                </span>
                <span>
                  <button
                    className={`compliance-enable-toggle ${policy.enabled === false ? "" : "active"}`}
                    type="button"
                    onClick={() => togglePolicyEnabled(policy)}
                    disabled={
                      savingPolicyId === savingKey ||
                      (!workspacePolicyId && !compliancePolicyId)
                    }
                    aria-pressed={policy.enabled !== false}
                  >
                    <b />
                    {savingPolicyId === savingKey
                      ? "Saving"
                      : policy.enabled === false
                        ? "Off"
                        : "On"}
                  </button>
                </span>
                <span>
                  <em className={`pill ${policyStatusTone(status)}`}>
                    {normalizeStatus(status)}
                  </em>
                </span>
                <span>{formatDateTime(latestRun)}</span>
                <span className="compliance-row-actions">
                  <button
                    className="mini-btn"
                    type="button"
                    onClick={() => setViewPolicy(policy)}
                  >
                    <i className="ti ti-eye" /> View
                  </button>
                </span>
              </article>
            );
          })}
          {loading ? (
            <div className="resident-inline-empty">
              Loading compliance policies...
            </div>
          ) : null}
          {!loading && !workspacePolicies.length ? (
            <div className="resident-inline-empty">
              No compliance policies found.
            </div>
          ) : null}
          {!loading &&
          workspacePolicies.length > 0 &&
          !visiblePolicies.length ? (
            <div className="resident-inline-empty">
              No compliance policies match the current filters.
            </div>
          ) : null}
        </div>
      </Panel>

      {viewPolicy ? (
        <div
          className="compliance-policy-detail-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="compliance-policy-detail-title"
        >
          <button
            className="compliance-policy-detail-drawer__backdrop"
            type="button"
            aria-label="Close compliance policy details"
            onClick={() => setViewPolicy(null)}
          />
          <aside className="compliance-policy-detail-drawer__panel">
            <header>
              <div>
                <span>{policyCode(viewPolicy) || "Compliance Policy"}</span>
                <h2 id="compliance-policy-detail-title">
                  {policyTitle(viewPolicy)}
                </h2>
              </div>
              <button
                className="mini-btn icon-only"
                type="button"
                aria-label="Close compliance policy details"
                onClick={() => setViewPolicy(null)}
              >
                <i className="ti ti-x" />
              </button>
            </header>
            <div className="compliance-policy-detail-drawer__body">
              <div className="compliance-policy-detail-priority">
                <div className="compliance-policy-detail-status-strip">
                  <div>
                    <span>Status</span>
                    <strong>
                      <em
                        className={`pill ${policyStatusTone(policyDetail(viewPolicy, "status"))}`}
                      >
                        {normalizeStatus(policyDetail(viewPolicy, "status"))}
                      </em>
                    </strong>
                  </div>
                  <div>
                    <span>Enabled</span>
                    <strong>
                      {viewPolicy.enabled === false ? "Off" : "On"}
                    </strong>
                  </div>
                  <div>
                    <span>Latest Run</span>
                    <strong>
                      {formatDateTime(
                        firstText(
                          viewPolicy.latestRunAt,
                          viewPolicy.lastRunAt,
                          viewPolicy.recentRun?.createdAt,
                          viewPolicy.recentRun?.runAt,
                        ),
                      )}
                    </strong>
                  </div>
                </div>

                <dl className="compliance-policy-detail-flow">
                  <div>
                    <dt>Description</dt>
                    <dd>{policyDetail(viewPolicy, "description") || "-"}</dd>
                  </div>
                  <div>
                    <dt>Expectation</dt>
                    <dd>{policyDetail(viewPolicy, "expectation") || "-"}</dd>
                  </div>
                  <div>
                    <dt>Threshold</dt>
                    <dd>{policyDetail(viewPolicy, "threshold") || "-"}</dd>
                  </div>
                  <div>
                    <dt>Reference</dt>
                    <dd>{policyDetail(viewPolicy, "reference") || "-"}</dd>
                  </div>
                </dl>
              </div>

              <section className="compliance-policy-run-results">
                <h3>Last Run Results</h3>
                <pre>{runDetails(viewPolicy)}</pre>
              </section>

              <details className="compliance-policy-detail-secondary">
                <summary>More policy data</summary>
                <dl>
                  <div>
                    <dt>State</dt>
                    <dd>{policyDetail(viewPolicy, "state") || "-"}</dd>
                  </div>
                  <div>
                    <dt>Country</dt>
                    <dd>{policyDetail(viewPolicy, "country") || "-"}</dd>
                  </div>
                  <div>
                    <dt>Metric</dt>
                    <dd>{policyDetail(viewPolicy, "metric") || "-"}</dd>
                  </div>
                  <div>
                    <dt>Rule Template</dt>
                    <dd>
                      <pre>
                        {prettyJson(
                          viewPolicy.ruleTemplate ||
                            viewPolicy.compliancePolicy?.ruleTemplate,
                        )}
                      </pre>
                    </dd>
                  </div>
                </dl>
              </details>
            </div>
          </aside>
        </div>
      ) : null}

      {bulkAction ? (
        <div
          className="compliance-bulk-confirm-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="compliance-bulk-confirm-title"
        >
          <button
            className="compliance-bulk-confirm-dialog__backdrop"
            type="button"
            aria-label="Cancel bulk compliance change"
            onClick={() => setBulkAction("")}
          />
          <section className="compliance-bulk-confirm-dialog__panel">
            <div className="compliance-bulk-confirm-dialog__icon">
              <i
                className={`ti ${bulkAction === "enable" ? "ti-toggle-right" : "ti-toggle-left"}`}
              />
            </div>
            <div>
              <h2 id="compliance-bulk-confirm-title">
                {bulkAction === "enable"
                  ? "Enable all compliance policies?"
                  : "Disable all compliance policies?"}
              </h2>
              <p>
                {bulkTargets.length}{" "}
                {bulkTargets.length === 1 ? "policy" : "policies"} will be{" "}
                {bulkAction === "enable" ? "enabled" : "disabled"} for this
                workspace.
              </p>
            </div>
            <div className="compliance-bulk-confirm-dialog__actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => setBulkAction("")}
                disabled={savingPolicyId.startsWith("bulk-")}
              >
                Cancel
              </button>
              <button
                className="mini-btn primary"
                type="button"
                onClick={runBulkAction}
                disabled={
                  !bulkTargets.length || savingPolicyId.startsWith("bulk-")
                }
              >
                <i
                  className={`ti ${savingPolicyId.startsWith("bulk-") ? "ti-loader-2" : "ti-check"}`}
                />
                Confirm
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </CareShell>
  );
}
